<template>
    <div class="comment-type-select-container">
        <div class="dropdown-select" ref="dropdownSelect" :disabled="props.disabled">
            <img 
                v-if="selectedOption.space_s3_key || selectedOption.space_icon"
                class="dropdown-select-img"
                :src="selectedOption.space_s3_key || icons[selectedOption.space_icon]"
                loading="lazy"
                alt="">
            <div class="dropdown-label">{{ props.selectedOption.label }}</div>
            <PzIcon class="icon-suffix"
                    :icon="isShown ? (closeIcon || 'SolidDropdownClose') : (openIcon || 'SolidDropdownOpen')" />
        </div>
        <div ref="dropdownOptions" class="comment-type-select-container_dropdown-options">
            <div class="dropdown-option"
                 :class="{ 'dropdown-option-selected': isSelected(option) }"
                 v-for="option in options" :key="`${option.key}_${option.extraKey}`"
                 :value="`${option.key}_${option.extraKey}`" @click="onSelect(option)">
                <img v-if="option.extraKey"
                     class="dropdown-option_space-img"
                     :src="option.space_s3_key || icons[option.space_icon]"
                     loading="lazy"
                     alt="">
                <div class="dropdown-option_label">{{ option.label }}</div>
                <div class="dropdown-option_icon">
                    <PzIcon icon="SolidTick" v-if="isSelected(option)" />
                </div>
            </div>
            <div v-if="extraLoading">
                <PzIcon icon="SolidLoadingBars" style="color: #505f79;margin-left: 8px; font-size: 20px;" />
            </div>
        </div>
    </div>
</template>
<script lang="ts" setup>
import { Icon, Popper } from 'patsnap-biz';

import '@patsnap-ui/icon/assets/solid/DropdownClose.svg';
import '@patsnap-ui/icon/assets/solid/DropdownOpen.svg';
import '@patsnap-ui/icon/assets/solid/tick.svg';
import '@patsnap-ui/icon/assets/solid/NoDataTip.svg';
import { computed, onMounted, ref } from 'vue';
import _ from 'lodash';
import { icons } from 'library-@/utils/workspaceIcon';

// @ts-ignore
const tippy = Popper.tippy;

const PzIcon = Icon

type IOptionItem = {
    label: string;
    key: string;
    extraKey?: string;
    space_s3_key?: string;
    space_icon?: string;
}

const props = defineProps<{
    options: IOptionItem[],
    selectedOption: IOptionItem,
    disabled?: boolean,
    icon?: string,
    openIcon?: string,
    closeIcon?: string,
    extraLoading: boolean,
    sourceIn: 'WORKSPACE' | 'ANALYTICS' | 'EUREKA' | 'VOTE',
}>()

const emits = defineEmits<{
    (event: 'change', option: IOptionItem): void;
}>();

const dropdownSelect = ref(null)
const dropdownOptions = ref(null)
const tippyInstance = ref(null);

const isShown = computed(() => {
    return _.get(tippyInstance.value, 'state.isShown', false)
})

const onSelect = (option) => {
    if (tippyInstance.value) {
        (tippyInstance.value as any).hide();
    }
    emits('change', option)
}

const isSelected = (option) => {
    return `${option.key}_${option.extraKey}` === `${props.selectedOption.key}_${props.selectedOption.extraKey}`
}

onMounted(() => {
    tippyInstance.value = tippy(dropdownSelect.value, {
        trigger: 'click',
        interactive: true,
        content: dropdownOptions.value,
        allowHTML: true,
        theme: 'light',
        arrow: false,
        appendTo: () => document.body,
        placement: 'bottom-start',
        offset: props.sourceIn === 'ANALYTICS' ? [16, 8] : [-12, 6],
    });
})

</script>
<style lang="scss">
.comment-type-select-container {
    padding: 4px 12px;
    border-radius: 4px;
    width: 320px;
    border: 1px solid $border-normal;

    .dropdown-select {
        height: 24px;
        padding: 4px 0 4px 8px;
        display: flex;
        flex-direction: row;
        align-items: center;
        cursor: pointer;
        border-radius: 4px;
        color: $text-primary;

        &-img {
            width: 20px;
            height: 20px;
            border-radius: 4px;
            margin-right: 8px;
            margin-left: -8px;
        }

        .icon-prefix {
            font-size: 16px;
            margin-right: 4px;
            margin-bottom: 0;
        }

        .dropdown-label {
            flex: 1;
        }

        .icon-suffix {
            font-size: 24px;
            margin-left: 4px;
            margin-bottom: 0;
        }
    }

    &_dropdown-options {
        max-height: 320px;
        overflow-y: auto;
        overflow-x: hidden;

        .dropdown-option {
            min-width: 280px;
            max-width: 420px;
            font-size: 14px;
            color: $text-primary;
            padding: 4px 8px;
            cursor: pointer;
            border-radius: 4px;
            display: flex;
            flex-direction: row;
            align-items: center;
            justify-content: space-between;

            &.dropdown-option-selected {
                color: $brand-hover;
            }

            &:hover {
                background: $bg-hover;
            }

            &_label {
                line-height: 24px;
                flex: 1;
            }
            &_space-img {
                width: 20px;
                height: 20px;
                border-radius: 4px;
                margin-right: 8px;
            }

            &_icon {
                width: 40px;
                display: flex;
                flex-direction: row;
                justify-content: center;
                align-items: center;

                .patsnap-biz-icon {
                    font-size: 22px;
                    margin-bottom: 0;
                }
            }
        }
    }
}
</style>
